/**
 * Which screen boot shows, as a pure decision table.
 *
 * WHY THIS IS NOT INSIDE Boot.tsx: the two consequential bugs this logic can
 * have are both invisible to a reader and to every other test in this package.
 *
 *  1. ORDER. `turnstileSiteKey === null` is blocking for an ANONYMOUS visitor
 *     (nobody can log in) and irrelevant to a signed-in owner (the widget
 *     never renders). Checking it before `me` has answered 401 locks every
 *     signed-in owner out of any environment that has no site key -- a
 *     one-line hoist, no test failure, a dead admin surface.
 *  2. WHOSE FAULT. /admin-config.json is served by the BFF itself, so a 404
 *     from a stale deploy is not the owner's network. "Bitte
 *     Netzwerkverbindung prüfen" would send them somewhere they cannot fix it.
 *
 * Extracted here so both are pinned by pure tests (test/bootState.test.ts)
 * rather than by a fourth jsdom suite, which this plan's DOM-test scope does
 * not have room for. Boot.tsx calls this and renders the answer; it decides
 * nothing itself.
 */
import { ApiError } from './api';
import type { AdminConfig } from './types';

/**
 * The five dead ends, each named after who has to act: `network` is the
 * owner's connection, `deployment` and `turnstileMissing` are the operator's,
 * `platform` is a 500 nobody in the browser can fix, `generic` is the honest
 * admission that we do not know.
 */
export type BlockingKind = 'network' | 'deployment' | 'platform' | 'turnstileMissing' | 'generic';

/**
 * The slice of a react-query result this decision reads. Structural on
 * purpose: a UseQueryResult satisfies it, and so does an object literal in a
 * test, so the table can be exercised without a QueryClient or a renderer.
 */
export interface BootQuery<T> {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: T | undefined;
}

/** Which query a retry button re-runs. `null` = the screen offers no retry. */
export type BootRetry = 'config' | 'me' | null;

export type BootScreen =
  | { view: 'spinner' }
  | { view: 'blocking'; kind: BlockingKind; retry: BootRetry }
  | { view: 'login'; siteKey: string }
  | { view: 'children' };

export function chooseBootScreen(
  config: BootQuery<AdminConfig>,
  me: BootQuery<unknown>,
): BootScreen {
  if (config.isPending || me.isPending) return { view: 'spinner' };

  if (config.isError) {
    // Only a request that never completed is a network screen; a STATUS answer
    // means the BFF is not serving its own config.
    const networkOnly = config.error instanceof ApiError && config.error.kind === 'network';
    return { view: 'blocking', kind: networkOnly ? 'network' : 'deployment', retry: 'config' };
  }

  if (me.isError) {
    const err = me.error;
    const kind = err instanceof ApiError ? err.kind : null;
    if (kind === 'deployment') return { view: 'blocking', kind: 'deployment', retry: null };
    if (kind === 'config') return { view: 'blocking', kind: 'platform', retry: null };
    if (kind === 'network') return { view: 'blocking', kind: 'network', retry: 'me' };
    if (err instanceof ApiError && err.status === 401) {
      // Only NOW does the site key matter. Moving this check above the 401
      // test is the ordering bug described at the top of this file.
      const siteKey = config.data?.turnstileSiteKey;
      if (!siteKey) return { view: 'blocking', kind: 'turnstileMissing', retry: null };
      return { view: 'login', siteKey };
    }
    return { view: 'blocking', kind: 'generic', retry: 'me' };
  }

  return { view: 'children' };
}
